import { languageDisplayCode, LanguagesEnum } from '@/api/enums';
import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  ContentItemType,
  CourseContent,
  CourseContentType,
  CourseFormState,
  ModifiedResource,
  Resource,
  ResourceType,
} from '@/client/courses';
import { QuizType } from '@/client/quizzes/types';
import { SystemRoles } from '@/client/users/types/roles.type';
import { LoadingStateType } from '@/common/constants';
import { DialogContext } from '@/common/context';
import { DefaultVideoLanguageDropdown } from '@/components/courses/DefaultVideoLanguageDropdown';
import { ContentQuizModal } from '@/components/quiz/modals/ContentQuizModal';
import { ContentSlideDeckModal } from '@/components/slides/admin/modals/ContentSlideDeckModal';
import { useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import { selectCurrentUser } from '@/store/features/users/current-user.slice';
import { DialogType } from '@/system-settings/enums';
import { AppButton } from '@/ui/buttons';
import { AppIconTooltip } from '@/ui/icon-tooltip';
import { LanguageTabs } from '@/ui/language-tabs';
import { FlexContainer } from '@/ui/styled-ui';
import { AppSwitch } from '@/ui/switch/AppSwitch';
import {
  convertFilesToArrayFormat,
  FeedbackIcon,
  GlobeIcon,
  hasAnyResourceTranslationFile,
  modifyCourseResource,
  PdfIcon,
  QuizIcon,
  ScormIcon,
  SlideDeckIcon,
  TrashIcon,
  VideoIcon,
} from '@/utils/helpers';
import { AxiosError } from 'axios';
import { without } from 'lodash';
import React, { useContext, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ContentResourceModal } from './modals/ContentResourceModal';

const StyledFlexContainer = styled(FlexContainer)<{
  index: number;
  isFirst?: boolean;
  isLast?: boolean;
}>`
  position: relative;

  h5 {
    color: var(--black-main);
    font-size: 18px;
    margin: 0;
  }

  .line {
    position: absolute;
    left: var(--small-padding);
    top: 0;
    width: 2px;
    height: calc(100% + var(--medium-padding));
    background: var(--white-main);
    z-index: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;
  }

  ${({ isFirst }) =>
    isFirst &&
    `.line {
        top: 50%;
        height: calc(50% + var(--medium-padding));
      }`}

  ${({ isLast }) =>
    isLast &&
    `.line {
        height: calc(50% + var(--medium-padding));
        top: -18px;
      }`}
`;

const StyledOrderIndicator = styled.div`
  background: var(--white-main);
  border-radius: 50%;
  display: flex;
  width: 26px;
  height: 26px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border: 2px solid var(--white-main);
  color: var(--black-main);
  font-size: var(--xxsmall-font-size);
  font-weight: 500;
  line-height: 150%;
  z-index: 1;
  position: relative;
  margin: 0;
  pointer-events: none;
`;

const StyledItem = styled(FlexContainer)`
  border-radius: var(--xsmall-border-radius);
  border: 1px solid var(--gray-dark);
  background: var(--white-main);
  padding: var(--default-padding);
  position: relative;
  margin-left: var(--default-padding);

  .title {
    font-weight: 600;
    margin-bottom: var(--small-padding);

    svg {
      margin-right: var(--small-padding);
    }
  }

  .languages {
    svg {
      margin-top: 3px;
    }
  }

  .actions {
    margin-top: var(--default-padding);

    button {
      height: 30px;
      padding: 0 28px;
      font-size: var(--xsmall-font-size);
      z-index: 999;

      .pi {
        font-size: var(--xsmall-font-size);
      }
    }

    .delete {
      display: flex;
      cursor: pointer;
      z-index: 999;

      &:hover {
        svg path {
          fill: var(--red-main);
        }
      }
    }
  }

  .default-video-dropdown,
  .read-confirmation {
    position: relative;
    z-index: 999;
    width: calc(100% + 2 * var(--default-padding));
    margin: var(--small-padding) calc(-1 * var(--default-padding))
      calc(-1 * var(--default-padding));
    padding: var(--small-padding) var(--default-padding);
    border-top: 1px solid var(--gray-dark);
  }
`;

type ContentItemProps = {
  index: number;
  values: CourseFormState;
  state?: LoadingStateType;
  isDisabled?: boolean;
  isSingle?: boolean;
  isDragging?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  setFieldValue: (field: string, value: any) => void;
  setIsSubmitDisabled: (isSubmitDisabled: boolean) => void;
};

const getContentType = (content: CourseContent): CourseContentType => {
  if (content.type === CourseContentType.QUIZ) {
    return CourseContentType.QUIZ;
  }

  if (content.type === CourseContentType.PRESENTATION) {
    return CourseContentType.PRESENTATION;
  }

  return CourseContentType.RESOURCE;
};

export const ContentItem: React.FC<ContentItemProps> = ({
  index,
  values,
  state,
  isDisabled,
  isSingle,
  isDragging,
  isFirst,
  isLast,
  setFieldValue,
  setIsSubmitDisabled,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { id: courseId } = useParams();
  const { setDialogData } = useContext(DialogContext);
  const currentUser = useAppSelector(selectCurrentUser);
  const [validOptions, setValidOptions] = useState<LanguagesEnum[]>([]);
  const [
    isReadConfirmationToggleSubmitting,
    setIsReadConfirmationToggleSubmitting,
  ] = useState(false);
  const readConfirmationToggleId = useId();
  const [requiredTranslations, setRequiredTranslations] = useState([
    languageDisplayCode[LanguagesEnum.EN],
  ]);
  const [isContentResourceModalVisible, setContentResourceModalVisible] =
    useState(false);
  const [isContentQuizModalVisible, setContentQuizModalVisible] =
    useState(false);
  const [isSlideDeckModalVisible, setSlideDeckModalVisible] = useState(false);
  const [isFallbackLanguageSubmitting, setIsFallbackLanguageSubmitting] =
    useState(false);

  useEffect(() => {
    if (values?.courseLanguages) {
      setRequiredTranslations(
        values.courseLanguages.map(
          (languageCode: string) => languageDisplayCode[languageCode],
        ),
      );
    }
    const contentItem =
      values.courseContents[index]?.quiz ||
      values.courseContents[index]?.resource ||
      values.courseContents[index]?.presentation;

    setValidOptions(
      (contentItem?.fullyTranslatedLanguages || []).map(
        (languageCode: LanguagesEnum) =>
          languageDisplayCode[languageCode] as LanguagesEnum,
      ),
    );
  }, [values, index]);

  const isVideoResource =
    values.courseContents[index]?.resource?.type === ResourceType.VIDEO;
  const fallbackOptions =
    isVideoResource && validOptions.length > 0
      ? requiredTranslations.filter(
          (lang) => !validOptions.includes(lang as LanguagesEnum),
        )
      : [];
  const videoResource = isVideoResource
    ? values.courseContents[index]?.resource
    : undefined;
  const videoUploadedLanguages = videoResource?.fullyTranslatedLanguages ?? [];
  const isCustomFromScratchContent =
    !!values.customContent &&
    values.customizationMetadata?.originalContentId == null &&
    !values.cloneMetadata?.isClone;
  const showDefaultVideoDropdown =
    isVideoResource &&
    isCustomFromScratchContent &&
    videoUploadedLanguages.length > 1 &&
    fallbackOptions.length > 0;

  const getContentItemTitle = (content?: CourseContent): string => {
    if (!content) return '';

    const fileName =
      content.quiz?.title ||
      content.resource?.title ||
      content.presentation?.title;

    if (fileName) {
      return fileName;
    }

    if (content?.type === ContentItemType.PRESENTATION) {
      return t('content.new.slidedeck');
    }

    if (content?.quiz?.type === QuizType.FEEDBACK) {
      return t('content.feedback');
    }

    if (content?.quiz?.type === QuizType.TRADITIONAL) {
      return t('content.new.quiz');
    } else {
      switch (content.resource?.type) {
        case ResourceType.VIDEO:
          return t('content.new.video');
        case ResourceType.PDF:
          return t('content.new.pdf');
        case ResourceType.SCORM:
          return t('content.new.scorm');
        default:
          return '';
      }
    }
  };

  const getContentIcon = (content?: CourseContent) => {
    if (!content) return null;

    if (content?.type === ContentItemType.PRESENTATION) {
      return <SlideDeckIcon />;
    }

    if (content?.quiz?.type === QuizType.FEEDBACK) {
      return <FeedbackIcon />;
    }

    if (content?.quiz?.type === QuizType.TRADITIONAL) {
      return <QuizIcon />;
    } else {
      switch (content.resource?.type) {
        case ResourceType.VIDEO:
          return <VideoIcon />;
        case ResourceType.PDF:
          return <PdfIcon />;
        case ResourceType.SCORM:
          return <ScormIcon />;
        default:
          return '';
      }
    }
  };

  const handleEditItem = () => {
    const content = values.courseContents[index];
    const contentType = getContentType(content);

    switch (contentType) {
      case CourseContentType.PRESENTATION:
        setSlideDeckModalVisible(true);
        break;
      case CourseContentType.QUIZ:
        setContentQuizModalVisible(true);
        break;
      default:
        setContentResourceModalVisible(true);
        break;
    }
  };

  const handleDelete = () => {
    setDialogData({
      show: true,
      type: DialogType.WARNING,
      header: t('content.delete.item.confirm.title'),
      message: t('content.delete.item.confirm.text'),
      showDialogConfirmation: false,
      confirmButtonLabel: t('content.delete.item.confirm.action'),
      onAccept: () =>
        setFieldValue(
          'courseContents',
          without(values.courseContents, values.courseContents[index]),
        ),
    });
  };

  const getActionButtonLabel = () => {
    const content = values.courseContents[index];
    const contentType = getContentType(content);
    const hasContent = content?.quiz?.id || content?.resource?.id;

    if (content?.type === ContentItemType.PRESENTATION) {
      return t('generic.edit');
    }

    return contentType === CourseContentType.QUIZ
      ? hasContent
        ? t('content.edit.quiz')
        : t('content.create.quiz')
      : hasContent
      ? t('generic.edit')
      : t('content.upload');
  };

  return (
    <>
      <StyledFlexContainer
        direction="row"
        index={index}
        isFirst={isFirst}
        isLast={isLast}
      >
        {!isSingle && (
          <div className="line" style={{ opacity: isDragging ? 0 : 1 }} />
        )}
        <StyledOrderIndicator>{index + 1}</StyledOrderIndicator>

        <StyledItem
          direction="column"
          gap="var(--xxxsmall-padding)"
          width="100%"
        >
          {/* Title */}
          <FlexContainer justify="flex-start" align="center" className="title">
            {getContentIcon(values.courseContents[index])}
            {getContentItemTitle(values.courseContents[index])}
          </FlexContainer>

          {/* Languages */}
          <FlexContainer
            gap="var(--semi-xsmall-padding)"
            justify="flex-start"
            align="flex-center"
            className="languages"
          >
            {Array.isArray(values.courseLanguages) &&
              values.courseLanguages.length > 0 && <GlobeIcon />}
            <FlexContainer
              gap="var(--semi-xsmall-padding)"
              justify="flex-start"
              wrap="wrap"
            >
              <LanguageTabs
                options={values.courseLanguages || []}
                requiredOptions={requiredTranslations}
                validOptions={validOptions}
                fallbackOptions={fallbackOptions}
                isDisabled={isDisabled}
                limitVisible={5}
                resetVisibility={isDragging}
              />
            </FlexContainer>
          </FlexContainer>

          {/* Actions */}
          {(() => {
            const isFeedback =
              values.courseContents[index]?.quiz?.type === QuizType.FEEDBACK;
            const isSuperOrDev =
              currentUser?.role?.code === SystemRoles.DEVELOPER ||
              currentUser?.role?.code === SystemRoles.SUPER_ADMIN;
            const showEdit = !isFeedback;
            const showTrash = !isDisabled && (!isFeedback || isSuperOrDev);

            if (!showEdit && !showTrash) {
              return null;
            }

            return (
              <FlexContainer
                justify="space-between"
                align="center"
                gap="var(--semi-xsmall-padding)"
                wrap="wrap"
                className="actions"
              >
                {showEdit &&
                  Array.isArray(values.courseLanguages) &&
                  values.courseLanguages.length > 0 && (
                    <AppButton
                      severity="secondary"
                      type="outlined"
                      icon={
                        values.courseContents[index]?.type !==
                          CourseContentType.QUIZ &&
                        values.courseContents[index]?.type !==
                          ContentItemType.PRESENTATION &&
                        'pi pi-upload'
                      }
                      iconPos="right"
                      label={getActionButtonLabel()}
                      state={state}
                      onClick={handleEditItem}
                      isDisabled={isDisabled}
                    />
                  )}
                {showTrash && (
                  <div className="delete ml-auto" onClick={handleDelete}>
                    <TrashIcon />
                  </div>
                )}
              </FlexContainer>
            );
          })()}

          {showDefaultVideoDropdown && (
            <DefaultVideoLanguageDropdown
              className="default-video-dropdown"
              validLanguages={videoUploadedLanguages}
              selectedLanguage={
                videoResource?.fallbackLanguage ??
                (videoUploadedLanguages.includes(LanguagesEnum.EN)
                  ? LanguagesEnum.EN
                  : videoUploadedLanguages[0])
              }
              isDisabled={isDisabled || isFallbackLanguageSubmitting}
              onChange={async (language) => {
                const item = values.courseContents[index];
                const currentResource = item?.resource as
                  | ModifiedResource
                  | undefined;
                if (!courseId || !currentResource?.id) return;

                setIsFallbackLanguageSubmitting(true);
                try {
                  const resourceData = {
                    id: currentResource.id,
                    title: currentResource.title,
                    type: currentResource.type,
                    fallbackLanguage: language,
                  } as unknown as Resource;

                  const response = await client.courses.updateCourseResource(
                    courseId,
                    currentResource.id,
                    resourceData,
                  );

                  const updated = modifyCourseResource(response);
                  if (updated) {
                    const updatedContents = [...values.courseContents];
                    updatedContents[index] = {
                      ...item,
                      resource: updated,
                    };
                    setFieldValue('courseContents', updatedContents);
                  }
                } catch (err) {
                  handleAxiosError(err as AxiosError, toast);
                } finally {
                  setIsFallbackLanguageSubmitting(false);
                }
              }}
            />
          )}

          {/* Read-confirmation section (PDF only) */}
          {values.courseContents[index]?.resource?.type ===
            ResourceType.PDF && (
            <FlexContainer
              justify="space-between"
              align="center"
              className="read-confirmation"
            >
              <FlexContainer
                align="center"
                gap="var(--xxxsmall-padding)"
                width="auto"
              >
                <label htmlFor={readConfirmationToggleId}>
                  {t('content.pdf.addCompletionCheckBox.label')}
                </label>
                <AppIconTooltip
                  target={`read-confirmation-info-${index}`}
                  content={t('content.pdf.addCompletionCheckBox.tooltip')}
                />
              </FlexContainer>
              <AppSwitch
                inputId={readConfirmationToggleId}
                aria-label={t('content.pdf.addCompletionCheckBox.label')}
                checked={
                  !!values.courseContents[index]?.resource
                    ?.requiresReadConfirmation
                }
                disabled={
                  isDisabled ||
                  isReadConfirmationToggleSubmitting ||
                  !hasAnyResourceTranslationFile(
                    values.courseContents[index]?.resource as
                      | ModifiedResource
                      | undefined,
                  )
                }
                onChange={async (e) => {
                  const newValue = !!e.value;
                  const item = values.courseContents[index];
                  const currentResource = item?.resource as
                    | ModifiedResource
                    | undefined;
                  if (!courseId || !currentResource?.id) return;

                  setIsReadConfirmationToggleSubmitting(true);
                  try {
                    const arrayShape =
                      convertFilesToArrayFormat(currentResource);
                    if (!arrayShape) return;

                    const resourceData = {
                      id: currentResource.id,
                      title: currentResource.title,
                      type: currentResource.type,
                      requiresReadConfirmation: newValue,
                      translations: (arrayShape.translations || []).map(
                        (translation) => ({
                          language: translation.language,
                          file:
                            typeof translation.file === 'string'
                              ? translation.file
                              : translation.file?.id,
                        }),
                      ),
                    } as unknown as Resource;

                    const response = await client.courses.updateCourseResource(
                      courseId,
                      currentResource.id,
                      resourceData,
                    );

                    const updated = modifyCourseResource(response);
                    if (updated) {
                      const updatedContents = [...values.courseContents];
                      updatedContents[index] = {
                        ...item,
                        resource: updated,
                      };
                      setFieldValue('courseContents', updatedContents);
                    }
                  } catch (err) {
                    handleAxiosError(err as AxiosError, toast);
                  } finally {
                    setIsReadConfirmationToggleSubmitting(false);
                  }
                }}
              />
            </FlexContainer>
          )}
        </StyledItem>
      </StyledFlexContainer>

      <ContentResourceModal
        index={index}
        values={values}
        visible={isContentResourceModalVisible}
        isDisabled={!!isDisabled}
        setFieldValue={setFieldValue}
        setIsSubmitDisabled={setIsSubmitDisabled}
        onHide={() => setContentResourceModalVisible(false)}
      />

      <ContentQuizModal
        index={index}
        values={values}
        visible={isContentQuizModalVisible}
        isDisabled={!!isDisabled}
        setFieldValue={setFieldValue}
        setIsSubmitDisabled={setIsSubmitDisabled}
        onHide={() => setContentQuizModalVisible(false)}
      />

      <ContentSlideDeckModal
        index={index}
        values={values}
        setFieldValue={setFieldValue}
        visible={isSlideDeckModalVisible}
        onHide={() => setSlideDeckModalVisible(false)}
      />
    </>
  );
};
